import { useState } from 'react';
import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Card, Seccion, EstadoVacio } from '@/components/Card';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { ParaQuien, type RenglonParaQuien } from '@/components/ParaQuien';
import { AvisoDesajuste } from '@/components/AvisoDesajuste';
import {
  verResumenDelDia,
  verProximosDias,
  fraseDelDia,
  type PlatilloPorVenir,
} from '@/db/repos/dashboard';
import { descartarPlatillo } from '@/db/repos/menu';
import { saldoActual } from '@/db/repos/cash';
import { completarProduccion, type TareaProduccion } from '@/db/repos/production';
import {
  pedidosPorRecetaEntre,
  verCuentasPorCobrar,
  type PedidoDeReceta,
} from '@/db/repos/orders';
import {
  hoy,
  sumarDias,
  formatearLargo,
  formatearMedio,
  nombreDia,
  formatearCorto,
} from '@/domain/dates';
import { formatearCentavos } from '@/domain/money';
import { mensajeDeError } from '@/db/tx';
import { AREAS, areaDisponible } from '@/app/areas';

/**
 * Tablero: al abrir la app, que hay que hacer hoy.
 *
 * El orden de las secciones es el orden de urgencia, no el del flujo de datos:
 * primero lo atrasado, luego lo que hay que cocinar hoy, luego lo que se entrega,
 * luego lo que falta comprar. Es la secuencia en que ella necesita las
 * respuestas por la manana.
 *
 * No agrega calculos propios: todo viene de `verResumenDelDia`, que reune lo que
 * cada area ya calcula.
 */
export function DashboardPage() {
  const fecha = hoy();
  const [error, setError] = useState<string | null>(null);

  const resumen = useLiveQuery(() => verResumenDelDia(fecha), [fecha]);
  const proximos = useLiveQuery(() => verProximosDias(fecha, 3), [fecha]);

  /**
   * Lo que de verdad esta pedido de cada platillo.
   *
   * La ventana es amplia porque el resumen mezcla dias: lo atrasado se vende
   * despues y lo de hoy se cocina para manana.
   */
  /**
   * Lo que le deben, para poder separar vendido de cobrado.
   *
   * No lleva rango: una deuda de hace tres semanas se sigue debiendo hoy.
   */
  const porCobrar = useLiveQuery(() => verCuentasPorCobrar(), []);

  /**
   * El dinero que de verdad hay, que no es lo mismo que lo que gano.
   *
   * Tampoco lleva rango: un saldo es acumulado, no de la semana. Va junto a
   * "vendido" y "te deben" para que las tres cifras se lean como lo que son:
   * tres cosas distintas y no tres versiones del mismo dinero.
   */
  const saldo = useLiveQuery(() => saldoActual(), []);

  const pedidos = useLiveQuery(
    () => pedidosPorRecetaEntre(sumarDias(fecha, -21), sumarDias(fecha, 21)),
    [fecha],
  );
  const pedidoDe = (tarea: TareaProduccion): PedidoDeReceta | undefined =>
    pedidos?.get(`${tarea.platillo.fechaVenta}|${tarea.platillo.recipeId}`);

  const paraQuien = (tarea: TareaProduccion): RenglonParaQuien => ({
    nombreReceta: tarea.nombreReceta,
    estimado: tarea.platillo.porcionesPlaneadas,
    pedido: pedidoDe(tarea),
  });

  return (
    <div className="space-y-6">
      <header>
        <p className="text-sm font-medium uppercase tracking-wide text-texto-tenue">Hoy</p>
        <h1 className="text-2xl font-bold capitalize leading-tight text-texto">
          {formatearLargo(fecha)}
        </h1>
        {resumen !== undefined && (
          <p className="mt-1 text-base font-medium text-marca-fuerte">{fraseDelDia(resumen)}</p>
        )}
      </header>

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo completar">
          {error}
        </Banner>
      )}

      {resumen === undefined && <p className="text-sm text-texto-suave">Cargando…</p>}

      {resumen !== undefined && (
        <>
          {resumen.sinMenu && (
            <Banner tono="info" titulo="Todavía no hay menú">
              Define qué vas a vender y la app te dice qué cocinar y qué comprar.
              <div className="mt-3">
                <Link to="/menu">
                  <Button variante="primario" completo={false}>
                    Armar el menú
                  </Button>
                </Link>
              </div>
            </Banner>
          )}

          {resumen.atrasados.length > 0 && (
            <Seccion titulo="⚠ Atrasado" descripcion="Se debió cocinar antes">
              <ParaQuien renglones={resumen.atrasados.map(paraQuien)} />
              <div className="space-y-2">
                {resumen.atrasados.map((tarea) => (
                  <TarjetaCocinar
                    key={tarea.platillo.id}
                    tarea={tarea}
                    urgente
                    pedido={pedidoDe(tarea)}
                    onError={setError}
                  />
                ))}
              </div>
            </Seccion>
          )}

          <Seccion titulo="🍳 Hoy cocinas" descripcion="Para entregar después">
            {resumen.preparar.length === 0 ? (
              <EstadoVacio
                titulo="Nada que cocinar hoy"
                descripcion="Ningún platillo del menú se prepara en este día."
              />
            ) : (
              <>
                <ParaQuien
                  titulo="Para quién cocinas"
                  renglones={resumen.preparar.map(paraQuien)}
                />
                <div className="space-y-2">
                  {resumen.preparar.map((tarea) => (
                    <TarjetaCocinar
                      key={tarea.platillo.id}
                      tarea={tarea}
                      pedido={pedidoDe(tarea)}
                      onError={setError}
                    />
                  ))}
                </div>
              </>
            )}
          </Seccion>

          <Seccion titulo="📦 Hoy entregas" descripcion="Se vende en este día">
            {resumen.entregas.length === 0 ? (
              <EstadoVacio
                titulo="Sin entregas hoy"
                descripcion="No hay ventas planeadas para este día."
              />
            ) : (
              <>
                <ParaQuien
                  titulo="Para quién entregas"
                  renglones={resumen.entregas.map(paraQuien)}
                />
                <div className="space-y-2">
                  {resumen.entregas.map((tarea) => (
                    <Card key={`e-${tarea.platillo.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-texto">{tarea.nombreReceta}</p>
                          {/*
                            Esta tarjeta se habia quedado leyendo el estimado del
                            menu mientras el bloque de arriba ya mostraba lo
                            pedido: la misma entrega con dos numeros distintos, 4
                            enchiladas arriba y 2 abajo. Manda lo pedido.
                          */}
                          {(() => {
                            const pedido = pedidoDe(tarea);
                            const porciones = pedido?.porciones ?? 0;
                            const estimado = tarea.platillo.porcionesPlaneadas;

                            if (pedido === undefined || pedido.cuantos === 0) {
                              return (
                                <p className="text-sm text-texto-tenue">
                                  Nadie ha pedido · el menú estima {estimado} ·{' '}
                                  {formatearCentavos(tarea.platillo.precioVentaCentavos)} cada una
                                </p>
                              );
                            }

                            return (
                              <>
                                <p className="text-sm text-texto-suave">
                                  {porciones} {porciones === 1 ? 'porción' : 'porciones'} ·{' '}
                                  {formatearCentavos(pedido.totalCentavos)}
                                </p>
                                {porciones !== estimado && (
                                  <p className="text-xs text-texto-tenue">
                                    el menú estimaba {estimado}
                                  </p>
                                )}
                              </>
                            );
                          })()}
                          {tarea.platillo.notas !== null && (
                            <p className="mt-1 text-xs italic text-texto-suave">
                              {tarea.platillo.notas}
                            </p>
                          )}
                        </div>
                        <span
                          className={`shrink-0 text-xs font-medium ${
                            tarea.yaCompletada ? 'text-exito' : 'text-aviso'
                          }`}
                        >
                          {tarea.yaCompletada ? '✓ cocinado' : 'sin cocinar'}
                        </span>
                      </div>
                    </Card>
                  ))}
                </div>

                <Card className="bg-superficie-suave">
                  <div className="flex items-baseline justify-between gap-3">
                    {/*
                      Sale de los PEDIDOS, con el precio de cada cliente, no del
                      estimado del menu por precio de lista. Asi cuadra con lo
                      que dicen Pedidos y Cuentas.
                    */}
                    <span className="text-sm text-texto-suave">
                      Vas a cobrar por {resumen.porcionesPedidasHoy}{' '}
                      {resumen.porcionesPedidasHoy === 1 ? 'porción pedida' : 'porciones pedidas'}
                    </span>
                    <span className="text-lg font-bold tabular-nums text-texto">
                      {formatearCentavos(resumen.ventaEsperadaCentavos)}
                    </span>
                  </div>
                  {resumen.ingresoDeHoyCentavos > 0 && (
                    <div className="mt-1 flex items-baseline justify-between gap-3">
                      <span className="text-sm text-texto-suave">Ya registrado</span>
                      <span className="text-sm font-semibold tabular-nums text-exito">
                        {formatearCentavos(resumen.ingresoDeHoyCentavos)}
                      </span>
                    </div>
                  )}
                </Card>
              </>
            )}
          </Seccion>

          {resumen.ventasPendientes.length > 0 && (
            <Seccion titulo="💰 Ventas por registrar" descripcion="De los platillos de hoy">
              <div className="space-y-2">
                {resumen.ventasPendientes.map((pendiente) => (
                  <Card key={pendiente.platillo.id}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-texto">{pendiente.nombreReceta}</p>
                        <p className="text-sm text-texto-suave">
                          {pendiente.porRegistrar} sin registrar
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              <Link to="/ventas">
                <Button>Registrar ventas</Button>
              </Link>
            </Seccion>
          )}

          <Seccion titulo="🛒 Falta comprar" descripcion="Para los próximos días">
            {resumen.faltantes.cuantos === 0 ? (
              <EstadoVacio
                titulo="No te falta nada"
                descripcion="Con tu inventario alcanza para lo que viene."
              />
            ) : (
              <Card>
                <p className="text-base font-semibold text-texto">
                  {resumen.faltantes.cuantos}{' '}
                  {resumen.faltantes.cuantos === 1 ? 'insumo' : 'insumos'} por comprar
                </p>
                <p className="mt-0.5 text-sm text-texto-suave">
                  {resumen.faltantes.primeros.join(', ')}
                  {resumen.faltantes.cuantos > resumen.faltantes.primeros.length && ' y más'}
                </p>
                {resumen.faltantes.costoAproximadoCentavos > 0 && (
                  <p className="mt-1 text-sm tabular-nums text-texto-suave">
                    Aproximado {formatearCentavos(resumen.faltantes.costoAproximadoCentavos)}
                    {resumen.faltantes.costoIncompleto && ' (falta algún precio)'}
                  </p>
                )}
                <div className="mt-3">
                  <Link to="/lista-compras">
                    <Button variante="secundario">Ver la lista</Button>
                  </Link>
                </div>
              </Card>
            )}
          </Seccion>

          {proximos !== undefined && proximos.length > 0 && (
            <Seccion titulo="Lo que viene" descripcion="Próximos días">
              <ul className="space-y-2">
                {proximos.map((dia) => (
                  <li key={dia.fecha}>
                    <Card>
                      <p className="text-sm font-semibold capitalize text-texto">
                        {nombreDia(dia.fecha)} {formatearCorto(dia.fecha)}
                      </p>
                      {/*
                        El numero entre parentesis es lo PEDIDO. Antes era el
                        estimado del menu, que decia "Enchiladas (4)" cuando habia
                        2 pedidos, contradiciendo al resto del tablero.
                      */}
                      {dia.preparaciones.length > 0 && (
                        <p className="mt-1 text-sm text-texto-suave">
                          🍳 cocinar{' '}
                          {dia.preparaciones.map((p) => textoPlatillo(p)).join(', ')}
                        </p>
                      )}
                      {dia.entregas.length > 0 && (
                        <p className="mt-0.5 text-sm text-texto-suave">
                          📦 entregar {dia.entregas.map((e) => textoPlatillo(e)).join(', ')}
                        </p>
                      )}
                    </Card>
                  </li>
                ))}
              </ul>
            </Seccion>
          )}

          {resumen.semana.cuantasVentas > 0 && (
            <Seccion titulo="📈 Esta semana">
              <Card
                className={
                  resumen.semana.resultadoNetoCentavos < 0
                    ? 'border-alerta bg-alerta-suave'
                    : 'bg-superficie-suave'
                }
              >
                {/*
                  "Vendido" y no "Ingreso": este numero nace al registrar la
                  venta, este pagada o no. Llamarlo ingreso invita a leerlo como
                  dinero en la mano, y parte puede seguir por cobrar.
                */}
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-texto-suave">Vendido</span>
                  <span className="font-semibold tabular-nums text-texto">
                    {formatearCentavos(resumen.semana.ingresoCentavos)}
                  </span>
                </div>
                <div className="mt-1 flex items-baseline justify-between gap-3">
                  <span className="text-sm text-texto-suave">
                    {resumen.semana.resultadoNetoCentavos < 0 ? 'Perdiste' : 'Te queda'}
                  </span>
                  <span
                    className={`text-lg font-bold tabular-nums ${
                      resumen.semana.resultadoNetoCentavos < 0 ? 'text-alerta' : 'text-texto'
                    }`}
                  >
                    {formatearCentavos(resumen.semana.resultadoNetoCentavos)}
                  </span>
                </div>
                {resumen.semana.costoIncompleto && (
                  <p className="mt-1 text-xs text-aviso">
                    Falta algún costo, así que el resultado es parcial
                  </p>
                )}

                {/*
                  Vender y cobrar son dos cosas. Este renglon es el puente: sin el,
                  el tablero deja creer que todo lo vendido ya entro a la bolsa.
                  El dato sale de los pedidos, que es donde vive el estado de pago.
                */}
                {porCobrar !== undefined && porCobrar.totalCentavos > 0 && (
                  <div className="mt-2 border-t border-borde pt-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-sm text-texto-suave">Te deben</span>
                      <span className="font-semibold tabular-nums text-aviso">
                        {formatearCentavos(porCobrar.totalCentavos)}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-texto-tenue">
                      No es de esta semana, es todo lo que llevas sin cobrar
                    </p>
                  </div>
                )}

                <div className="mt-3 space-y-2">
                  <Link to="/ganancias">
                    <Button variante="secundario">Ver ganancias</Button>
                  </Link>
                  {porCobrar !== undefined && porCobrar.totalCentavos > 0 && (
                    <Link to="/pedidos">
                      <Button variante="fantasma">Ver a quién le cobras</Button>
                    </Link>
                  )}
                </div>
              </Card>
            </Seccion>
          )}

          {/*
            Se muestra siempre, incluso en cero: un saldo en blanco es un dato,
            y esconderlo cuando no hay ventas de la semana dejaria el tablero
            hablando solo de ganancia.
          */}
          {saldo !== undefined && (
            <Seccion titulo="💵 Tu caja">
              <Card
                className={
                  saldo.saldoCentavos < 0 ? 'border-alerta bg-alerta-suave' : 'bg-superficie-suave'
                }
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-texto-suave">Tienes disponible</span>
                  <span
                    className={`text-lg font-bold tabular-nums ${
                      saldo.saldoCentavos < 0 ? 'text-alerta' : 'text-texto'
                    }`}
                  >
                    {formatearCentavos(saldo.saldoCentavos)}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-texto-tenue">
                  Cobrado menos lo que has gastado. No incluye lo que te deben ni la despensa.
                </p>

                {saldo.saldoCentavos < 0 && (
                  <p className="mt-2 text-xs font-medium text-alerta">
                    Está en rojo porque has puesto dinero tuyo para comprar.
                  </p>
                )}

                {saldo.aportacionNetaCentavos > 0 && (
                  <div className="mt-2 flex items-baseline justify-between gap-3 border-t border-borde pt-2">
                    <span className="text-sm text-texto-suave">Tu dinero adentro</span>
                    <span className="font-semibold tabular-nums text-texto">
                      {formatearCentavos(saldo.aportacionNetaCentavos)}
                    </span>
                  </div>
                )}

                <div className="mt-3">
                  <Link to="/caja">
                    <Button variante="secundario">Ver caja</Button>
                  </Link>
                </div>
              </Card>
            </Seccion>
          )}

          {resumen.inventario.sinCostoConocido > 0 && (
            <Banner
              tono="info"
              titulo={`${resumen.inventario.sinCostoConocido} ${
                resumen.inventario.sinCostoConocido === 1 ? 'insumo' : 'insumos'
              } sin costo`}
            >
              Sus cantidades cuentan para cocinar, pero no entran en tus costos. Registra una
              compra de ellos y los números se completan solos.
            </Banner>
          )}
        </>
      )}

      <Seccion titulo="Todo" descripcion="Accesos directos a cada área">
        <ul className="grid grid-cols-2 gap-3">
          {AREAS.map((area) => {
            const disponible = areaDisponible(area);
            return (
              <li key={area.ruta}>
                <Link
                  to={area.ruta}
                  className={[
                    'flex min-h-24 flex-col justify-between rounded-panel border p-3',
                    disponible
                      ? 'border-borde bg-superficie active:bg-superficie-suave'
                      : 'border-borde border-dashed bg-superficie-suave',
                  ].join(' ')}
                >
                  <span aria-hidden="true" className="text-2xl leading-none">
                    {area.icono}
                  </span>
                  <span className="mt-2">
                    <span
                      className={`block text-sm font-semibold ${
                        disponible ? 'text-texto' : 'text-texto-suave'
                      }`}
                    >
                      {area.titulo}
                    </span>
                    {!disponible && (
                      <span className="mt-0.5 block text-xs text-texto-tenue">
                        Fase {area.fase}
                      </span>
                    )}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </Seccion>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsCocinar {
  tarea: TareaProduccion;
  urgente?: boolean;
  /** Lo que de verdad esta pedido: el menu dice una intencion, no pedidos. */
  pedido?: PedidoDeReceta;
  onError: (mensaje: string | null) => void;
}

/**
 * Tarjeta de un platillo por cocinar, con el boton que lo marca como hecho.
 *
 * Se puede completar SIN salir del tablero (Req. 17.5): por la manana, con las
 * manos ocupadas, navegar a otra pantalla es friccion que se paga en captura no
 * hecha.
 */
/**
 * Un platillo de "Lo que viene", con el numero que manda.
 *
 * Entre parentesis va lo PEDIDO. Si nadie ha pedido se dice con palabras en lugar
 * de poner un cero pelon, que se leeria como si no hubiera que cocinar. Y cuando
 * lo pedido no coincide con el estimado se muestran los dos, porque en esa
 * diferencia esta el aviso de quedarse corto de comida.
 */
function textoPlatillo(p: PlatilloPorVenir): string {
  if (p.cuantosPidieron === 0) return `${p.nombre} (nadie ha pedido, se estiman ${p.porcionesEstimadas})`;
  if (p.porcionesPedidas === p.porcionesEstimadas) return `${p.nombre} (${p.porcionesPedidas})`;
  return `${p.nombre} (${p.porcionesPedidas} pedidas, se estimaban ${p.porcionesEstimadas})`;
}

function TarjetaCocinar({ tarea, urgente = false, pedido, onError }: PropsCocinar) {
  const [guardando, setGuardando] = useState(false);
  const [avisos, setAvisos] = useState<string[]>([]);

  async function cocinar() {
    setGuardando(true);
    onError(null);
    try {
      /**
       * Se cocina lo PEDIDO, no lo que estima el menu. Sin pedidos anotados se
       * usa el estimado, que es lo unico que hay.
       */
      const resultado = await completarProduccion(tarea.platillo.id, {
        ...(pedido !== undefined && pedido.porciones > 0
          ? { porciones: pedido.porciones }
          : {}),
      });
      if (resultado.advertencias.length > 0) setAvisos(resultado.advertencias);
    } catch (e) {
      onError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  async function noSeCocino() {
    setGuardando(true);
    onError(null);
    try {
      await descartarPlatillo(tarea.platillo.id, 'No se cocinó: no hubo pedidos.');
    } catch (e) {
      onError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Card className={urgente ? 'border-alerta bg-alerta-suave' : ''}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-texto">{tarea.nombreReceta}</p>
          {/*
            Manda lo pedido, no el numero del menu: aqui solo se cocina lo que la
            gente pidio, y el numero del menu es un estimado para escalar la
            receta.
          */}
          {pedido !== undefined && pedido.cuantos > 0 ? (
            <>
              <p className="text-sm font-semibold text-marca-fuerte">
                {pedido.porciones} {pedido.porciones === 1 ? 'porción pedida' : 'porciones pedidas'}
              </p>
              <p className="text-sm text-texto-suave">
                {pedido.clientes.map((c) => `${c.clienteNombre} ${c.porciones}`).join(', ')}
              </p>
            </>
          ) : (
            <p className="text-sm font-medium text-texto-tenue">
              Nadie ha pedido todavía · el menú estima {tarea.platillo.porcionesPlaneadas}
            </p>
          )}
          <p className="mt-0.5 text-xs text-texto-tenue">
            Se entrega el {formatearMedio(tarea.platillo.fechaVenta)} · costo{' '}
            {formatearCentavos(tarea.costoTotalCentavos)}
            {tarea.costoIncompleto && ' (incompleto)'}
          </p>
          {tarea.platillo.notas !== null && (
            <p className="mt-1 text-xs italic text-texto-suave">{tarea.platillo.notas}</p>
          )}
        </div>

        {tarea.yaCompletada && (
          <span className="shrink-0 text-xs font-medium text-exito">✓ hecho</span>
        )}
      </div>

      {/*
        El aviso que faltaba: si lo cocinado no coincide con lo pedido, hay que
        verlo aqui y no descubrirlo repartiendo.
      */}
      <AvisoDesajuste
        cocinadas={tarea.yaCompletada ? (tarea.produccion?.porcionesAProducir ?? null) : null}
        pedido={pedido}
      />

      {tarea.faltaInventario && !tarea.yaCompletada && (
        <p className="mt-2 text-xs font-medium text-alerta">
          ⚠ No alcanza{' '}
          {tarea.ingredientes
            .filter((i) => i.insuficiente)
            .map((i) => i.nombreInsumo)
            .join(', ')}
        </p>
      )}

      {/*
        La salida al aviso, puesta DONDE APARECE EL AVISO.
        La accion existia solo en Menu, y un platillo atrasado es de dias atras:
        habia que navegar semanas hacia atras para apagar una alerta que estorba
        aqui. El boton tiene que estar donde esta el problema.
      */}
      {urgente && !tarea.yaCompletada && (
        <Button
          variante="secundario"
          disabled={guardando}
          onClick={() => void noSeCocino()}
        >
          No lo cociné, no hubo pedidos
        </Button>
      )}

      {avisos.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-aviso">
          {avisos.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      )}

      {!tarea.yaCompletada && (
        <div className="mt-3 flex gap-2">
          <Button
            completo={false}
            disabled={guardando || tarea.ingredientes.length === 0}
            onClick={() => void cocinar()}
          >
            {guardando ? 'Guardando…' : 'Ya lo cociné'}
          </Button>
          <Link to="/produccion">
            <Button variante="fantasma" completo={false}>
              Ver detalle
            </Button>
          </Link>
        </div>
      )}
    </Card>
  );
}
